import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/context/LanguageContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FooterDeveloperCredit } from "@/components/FooterDeveloperCredit";
import { Phone, Mail, MapPin } from "lucide-react";
import { SITE, NAV_LINKS, telHref, waHref, DEFAULT_WA_TEXT } from "@/config/site";
import { canonicalPath } from "@/lib/canonicalPath";
import { PRIMARY_LOCATIONS, SECONDARY_LOCATIONS } from "@/config/locations";

const Num = ({ data, label, testPrefix }) => {
  const { t } = useLanguage();
  return (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">{t(label)}</span>
    <div className="flex flex-wrap items-center gap-3">
      <a
        href={telHref(data.tel)}
        data-testid={`${testPrefix}-call-link`}
        className="inline-flex items-center gap-1.5 text-slate-200 hover:text-white transition-colors"
      >
        <Phone className="h-4 w-4 text-sky-400" aria-hidden="true" />
        {data.display}
      </a>
      <a
        href={waHref(data.wa, t(DEFAULT_WA_TEXT))}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testPrefix}-whatsapp-link`}
        className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 transition-colors"
      >
        <WhatsAppIcon className="h-4 w-4" aria-hidden="true" />
        WhatsApp
      </a>
    </div>
  </div>
  );
};

export const Footer = ({ renderedYear }) => {
  const { t, cityName, localizePath } = useLanguage();
  const [year, setYear] = useState(renderedYear ?? new Date().getFullYear());
  useEffect(() => { setYear(new Date().getFullYear()); }, []);
  return (
  <footer className="bg-[#071426] text-slate-300 border-t border-slate-800" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <OptimizedImage
            src="/logo.png"
            sizes="80px"
            alt={t("Jitendra Singh Tour Operator Service logo")}
            className="h-20 w-auto mb-4"
            width="512"
            height="512"
          />
          <p className="text-sm leading-relaxed text-slate-400">
            {t("Local, outstation and group taxi & Tempo Traveller service based in Satna, Madhya Pradesh. Call or WhatsApp for a quick quote.")}
          </p>
          <div className="mt-4 flex items-start gap-2 text-sm text-slate-400">
            <MapPin className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" aria-hidden="true" />
            <span>{t(SITE.addressShort)}</span>
          </div>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="footer-email-link"
            className="mt-2 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
          >
            <Mail className="h-4 w-4 text-amber-400" aria-hidden="true" />
            {SITE.email}
          </a>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">{t("Explore")}</h3>
          <ul className="space-y-2 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.path}>
                <Link
                  to={localizePath(canonicalPath(l.path))}
                  data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-slate-400 hover:text-amber-400 transition-colors"
                >
                  {t(l.label)}
                </Link>
              </li>
            ))}
            <li>
              <Link to={localizePath("/booking-terms/")} className="text-slate-400 hover:text-amber-400 transition-colors" data-testid="footer-terms-link">
                {t("Booking Terms & Privacy")}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">{t("Service Areas")}</h3>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <li>
              <Link to={localizePath("/")} data-testid="footer-location-satna" className="text-slate-400 hover:text-amber-400 transition-colors">
                {t("Satna (Base)")}
              </Link>
            </li>
            {PRIMARY_LOCATIONS.map((l) => (
              <li key={l.slug}>
                <Link to={localizePath(canonicalPath(l.slug))} className="text-slate-400 hover:text-amber-400 transition-colors">
                  {cityName(l.city)}
                </Link>
              </li>
            ))}
            {SECONDARY_LOCATIONS.map((l) => (
              <li key={l.slug}>
                <Link to={localizePath(canonicalPath(l.slug))} className="text-slate-500 hover:text-amber-400 transition-colors">
                  {cityName(l.city)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">{t("Call / WhatsApp")}</h3>
          <div className="space-y-4">
            <Num data={SITE.phonePrimary} label="Primary" testPrefix="footer-primary" />
            <Num data={SITE.phoneSecondary} label="Secondary" testPrefix="footer-secondary" />
          </div>
        </div>
      </div>

      <div className="mt-12 border-t border-slate-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
        <p>{t("© {year} {brand}. All rights reserved.", { year, brand: SITE.brand })}</p>
        <p>{t("Based in Satna, Madhya Pradesh · Serving Central India")}</p>
      </div>
      <FooterDeveloperCredit />
    </div>
  </footer>
  );
};
